/**
 * TanStack column defs for the admin Users table. Sortable column ids are
 * the backend sort keys (crud_user._apply_sort): name, id, email, role.
 */
import type { IUser } from "@/api";
import { UserScopeCount } from "@/components/admin/table/cells/UserScopeCount";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router";

import { getInitials } from "../utils/getInitials";
import { InvitationStatusCell } from "./InvitationStatusCell";
import { UserRowActions } from "./UserRowActions";

export const columns: ColumnDef<IUser>[] = [
    {
        id: "select",
        enableSorting: false,
        meta: { cellClassName: "w-12" },
        header: ({ table }) => (
            <Checkbox
                aria-label="Select all"
                checked={table.getIsAllPageRowsSelected()}
                indeterminate={
                    table.getIsSomePageRowsSelected() &&
                    !table.getIsAllPageRowsSelected()
                }
                onCheckedChange={(checked) =>
                    table.toggleAllPageRowsSelected(Boolean(checked))
                }
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                aria-label={`Select ${row.original.first_name} ${row.original.last_name}`}
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onCheckedChange={(checked) =>
                    row.toggleSelected(Boolean(checked))
                }
            />
        ),
    },
    {
        id: "name",
        header: "Name",
        enableSorting: true,
        accessorFn: (user) => `${user.first_name} ${user.last_name}`,
        cell: ({ row }) => {
            const user = row.original;
            return (
                <div className="flex min-w-0 items-center gap-2">
                    {/* Decorative: the link text already names the user. */}
                    <Avatar className="size-7" aria-hidden="true">
                        <AvatarFallback className="text-xs">
                            {getInitials(user.first_name, user.last_name)}
                        </AvatarFallback>
                    </Avatar>
                    <Link
                        to={`${user.id}/details`}
                        className="truncate font-medium hover:underline"
                    >
                        {user.first_name} {user.last_name}
                    </Link>
                </div>
            );
        },
    },
    {
        accessorKey: "id",
        header: "ID",
        enableSorting: true,
        sortDescFirst: false,
    },
    { accessorKey: "email", header: "Email Address", enableSorting: true },
    {
        id: "invitation_status",
        header: "Invitation Status",
        enableSorting: false,
        cell: ({ row }) => <InvitationStatusCell user={row.original} />,
    },
    {
        id: "number_of_scopes",
        header: "Number of Scopes",
        enableSorting: false,
        cell: ({ row }) => <UserScopeCount userId={row.original.id} />,
    },
    {
        id: "role",
        header: "Permission",
        enableSorting: true,
        accessorFn: (user) => (user.is_admin ? "Admin" : "User"),
    },
    {
        id: "organizations",
        header: "Organization",
        enableSorting: false,
        accessorFn: (user) => user.organizations?.[0]?.name ?? "-",
    },
    {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        meta: { cellClassName: "w-12 text-right" },
        cell: ({ row }) => <UserRowActions user={row.original} />,
    },
];
